import type { ReactNode } from "react";
import { Badge, IconButton } from "@panelgenie/shared-ui";
import type { InvocationSummary } from "../types.js";
import { EyeIcon, ResubmitIcon, TrashIcon } from "./icons.js";
import {
  clerkUserUrl,
  formatCost,
  formatInstant,
  formatTokens,
  kindLabel,
  latencyBreakdown,
  outcomeBadgeVariant,
  outcomeLabel,
} from "../lib/format.js";

/**
 * The list table — one row per `support.listInvocations` summary,
 * most-recent-first (the server already sorts). Each row links to the
 * detail view by id; the raw response is *not* in the summary, so this
 * surface only shows the stamp + outcome and defers the photos / raw
 * response to the detail fetch.
 */

export type InvocationTableProps = {
  readonly items: readonly InvocationSummary[];
  readonly onSelect: (id: string) => void;
  /**
   * Fired when the row's Resubmit button is pressed (panel-scan rows
   * only). Absent → no Resubmit button is rendered at all, so surfaces
   * that embed the table read-only stay read-only.
   */
  readonly onResubmit?: ((id: string) => void) | undefined;
  /** Id of the row whose resubmit is in flight; disables its button. */
  readonly resubmittingId?: string | null | undefined;
  /**
   * Fired when the row's Delete button is pressed. Absent → no Delete
   * button is rendered, so read-only embeds of the table stay read-only.
   */
  readonly onDelete?: ((id: string) => void) | undefined;
  /** Id of the row whose delete is in flight; disables its button. */
  readonly deletingId?: string | null | undefined;
  /**
   * Clerk dashboard deep-link template (`…/users/{userId}`). When set,
   * the User column links to the user's Clerk entry; absent → plain text.
   */
  readonly clerkDashboardUserUrlTemplate?: string | undefined;
};

export function InvocationTable({
  items,
  onSelect,
  onResubmit,
  resubmittingId,
  onDelete,
  deletingId,
  clerkDashboardUserUrlTemplate,
}: InvocationTableProps) {
  if (items.length === 0) {
    return (
      <p aria-label="invocations-empty" className="text-base text-mute">
        No invocations match these filters.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table aria-label="invocations-table" className="w-full text-left text-sm">
        <thead className="border-b border-line bg-subtle text-mute">
          <tr>
            <Th>When</Th>
            <Th>User</Th>
            <Th>Kind</Th>
            <Th>Outcome</Th>
            <Th>Latency</Th>
            <Th>Tokens</Th>
            <Th>Est. $</Th>
            <Th>Photos</Th>
            <Th>
              <span className="sr-only">Actions</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-b border-line last:border-b-0">
              <Td className="whitespace-nowrap text-mute">
                {formatInstant(item.createdAt)}
              </Td>
              <UserCell
                userId={item.userId}
                userEmail={item.userEmail}
                clerkUrl={clerkUserUrl(clerkDashboardUserUrlTemplate, item.userId)}
              />
              <Td className="whitespace-nowrap">{kindLabel(item.kind)}</Td>
              <Td>
                <Badge variant={outcomeBadgeVariant(item.outcome)}>
                  {outcomeLabel(item.outcome)}
                </Badge>
              </Td>
              <LatencyCell
                latencyMs={item.latencyMs}
                backoffMs={item.backoffMs}
                retryCount={item.retryCount}
              />
              <Td className="whitespace-nowrap text-mute">
                {formatTokens(item.usage)}
              </Td>
              <Td className="whitespace-nowrap text-mute">
                {formatCost(item.usage, item.modelId)}
              </Td>
              <Td className="text-mute">{item.photoIds.length}</Td>
              <Td>
                {/* A "processing" row is a scan job, not a recorded
                    invocation — there is no detail to view yet and a
                    resubmit would double-run it, so both actions hide. */}
                {item.outcome !== "processing" && (
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      icon={<EyeIcon />}
                      label={`View invocation ${item.id}`}
                      title="View"
                      onClick={() => onSelect(item.id)}
                    />
                    {onResubmit !== undefined && item.kind === "panel-scan" && (
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={<ResubmitIcon />}
                        label={`Resubmit invocation ${item.id}`}
                        title={
                          resubmittingId === item.id ? "Resubmitting…" : "Resubmit"
                        }
                        disabled={resubmittingId === item.id}
                        onClick={() => onResubmit(item.id)}
                      />
                    )}
                    {onDelete !== undefined && (
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={<TrashIcon />}
                        label={`Delete invocation ${item.id}`}
                        title={deletingId === item.id ? "Deleting…" : "Delete"}
                        disabled={deletingId === item.id}
                        onClick={() => onDelete(item.id)}
                        className="text-mute hover:text-danger"
                      />
                    )}
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The User column. Prefers the resolved email (friendlier than the raw
 * Clerk id) and falls back to the id when no email resolved. When a
 * Clerk dashboard template is configured, the label links to that
 * user's Clerk entry. The exact `userId` is always kept in the tooltip
 * so staff can still copy it into the User-ID filter.
 */
function UserCell({
  userId,
  userEmail,
  clerkUrl,
}: {
  readonly userId: string;
  readonly userEmail: string | null;
  readonly clerkUrl: string | undefined;
}) {
  const label = userEmail ?? userId;
  // Email reads naturally; a raw id stays monospace for scannability.
  const labelClass = userEmail !== null ? "text-xs" : "font-mono text-xs";
  return (
    <Td className="max-w-44 truncate" title={userId}>
      {clerkUrl !== undefined ? (
        <a
          href={clerkUrl}
          target="_blank"
          rel="noreferrer"
          className={`${labelClass} text-accent hover:underline`}
        >
          {label}
        </a>
      ) : (
        <span className={labelClass}>{label}</span>
      )}
    </Td>
  );
}

/**
 * The Latency column. Shows real provider latency as the primary value and,
 * when this row's invocation incurred retry/backoff sleep (KAN-65), a muted
 * secondary line with the backoff time + retry count. Legacy rows (no backoff
 * recorded) and clean calls (zero backoff) show the single total only.
 */
function LatencyCell({
  latencyMs,
  backoffMs,
  retryCount,
}: {
  readonly latencyMs: number | null;
  readonly backoffMs: number | null;
  readonly retryCount: number | null;
}) {
  const { primary, backoff } = latencyBreakdown(latencyMs, backoffMs, retryCount);
  return (
    <Td className="whitespace-nowrap text-mute">
      <span>{primary}</span>
      {backoff !== null && (
        <span className="block text-xs text-mute opacity-70">{backoff}</span>
      )}
    </Td>
  );
}

function Th({ children }: { readonly children: ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function Td({
  children,
  className,
  title,
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly title?: string;
}) {
  return (
    <td className={`px-3 py-2 align-middle ${className ?? ""}`} title={title}>
      {children}
    </td>
  );
}
